import React, { useEffect, useState } from 'react';
import {
  electTestItemReferenceImage,
  type ResolvedReferenceImage,
} from '@app.raytd.com/store';
import { toast } from 'sonner';
import { usePhotoViewer } from '@/app/contexts/PhotoViewerContext';
import { ImageType } from '@/types/Images';

const toGalleryImage = (img: ResolvedReferenceImage): ImageType => ({
  id: img.id,
  name: img.display_name || img.name || 'Reference image',
  size: 0,
  mime_type: '',
  uri: img.url || img.preview_url || img.thumbnail_url || '',
  thumbnail_url: img.thumbnail_url || undefined,
  preview_url: img.preview_url || undefined,
});

interface TestItemReferenceImagesProps {
  testItemId: number;
  images: ResolvedReferenceImage[];
  /**
   * Inspection / report-authoring context: render an election toggle per image
   * (including `available` ones offered off). When false (read-only report
   * render) only the included images are shown, without toggles.
   */
  editable: boolean;
  /**
   * True when the images shown are a spec-level preview for a Test Item that
   * hasn't been saved yet (no id to elect against). Toggles render disabled
   * until the item is saved and a real election can be persisted.
   */
  disabled?: boolean;
  /**
   * Overrides how an election is persisted. Used when the Test Item doesn't
   * have an id yet (unsaved) — the caller buffers the choice locally and
   * flushes it once the item is created, instead of calling the API directly.
   */
  onElect?: (image: ResolvedReferenceImage, included: boolean) => void;
  /**
   * Read-only (report) render only: mirrors the caller's regular-image size
   * so reference thumbnails match the captured photos next to them —
   * 'sm' in-app, 'md' in print mode (same convention as ImageRow).
   */
  imageSize?: 'sm' | 'md';
}

/**
 * Renders the Reference Images resolved onto a Test Item. In an editable
 * context each is badged "Reference" to differentiate it from the item's own
 * captured photos, and the inspector can elect `available` images on or
 * remove defaulted ones; the choice is persisted per item (ADR-0003). In the
 * read-only report render the images sit under a "References" caption
 * instead, without the per-image badge.
 */
const TestItemReferenceImages: React.FC<TestItemReferenceImagesProps> = ({
  testItemId,
  images,
  editable,
  disabled = false,
  onElect,
  imageSize = 'sm',
}) => {
  // Local election state so toggles feel immediate; reseeded if props change.
  const [included, setIncluded] = useState<Record<number, boolean>>({});
  const { openPhotoViewer } = usePhotoViewer();

  useEffect(() => {
    setIncluded(Object.fromEntries(images.map((img) => [img.id, img.included])));
  }, [images]);

  const visible = editable ? images : images.filter((img) => included[img.id] ?? img.included);

  if (visible.length === 0) {
    return null;
  }

  // The two render contexts (in-app election UI vs. read-only report/PDF)
  // diverge on styling and which affordances show; computed once here so
  // adding another report-only tweak doesn't mean adding another `editable`
  // branch scattered through the JSX below.
  const variant = editable
    ? {
        showHeading: false,
        itemClassName: 'flex items-center gap-2 rounded-md border p-2',
        thumbnailClassName: 'h-12 w-12 rounded object-cover',
        showBadge: true,
      }
    : {
        // Column, not row: a row layout squeezes the title into whatever sliver is left beside
        // a fixed 80/128px image inside a 2-up grid cell — often very narrow itself (e.g. the
        // SDA compact row's Requirement column) — so the title wrapped into many narrow, tall
        // lines (raytd/web QA 108.02, item 6). Stacking the title below the image gives it the
        // cell's full width to wrap in, regardless of how narrow the surrounding column is.
        showHeading: true,
        itemClassName: 'flex flex-col items-start gap-1',
        thumbnailClassName:
          imageSize === 'md' ? 'h-32 w-32 object-cover' : 'h-20 w-20 object-cover',
        showBadge: false,
      };

  const toggle = async (image: ResolvedReferenceImage) => {
    const next = !(included[image.id] ?? image.included);
    setIncluded((prev) => ({ ...prev, [image.id]: next }));

    if (onElect) {
      onElect(image, next);
      return;
    }

    try {
      await electTestItemReferenceImage(testItemId, image.id, next);
    } catch {
      // Revert the optimistic flip on failure.
      setIncluded((prev) => ({ ...prev, [image.id]: !next }));
      toast.error('Failed to update reference image');
    }
  };

  return (
    <div data-testid="test-item-reference-images">
      {/* Report render (read-only): a distinct captioned block, visually separating
          the supplied standard from the inspector's own captured evidence (decision 7). */}
      {variant.showHeading && (
        <div className="mb-2 text-xs text-zinc-400 font-light break-before-avoid">
          References
        </div>
      )}
      <ul className="grid grid-cols-2 gap-3">
        {visible.map((img) => {
          const isOn = included[img.id] ?? img.included;
          // Report render shows reference images (often standards diagrams /
          // line-art that pixelate badly) at 80–128px, so it must draw from the
          // higher-resolution preview source — the same order the gallery uses
          // (toGalleryImage) — rather than the tiny thumbnail. The editable
          // chip is only 48px, where the thumbnail is the right, lighter source.
          const src = editable
            ? img.thumbnail_url
            : img.preview_url || img.url || img.thumbnail_url;
          return (
            <li key={img.id} className={variant.itemClassName}>
              {src && (
                <img
                  src={src}
                  alt={img.name ?? 'reference image'}
                  className={variant.thumbnailClassName}
                  onClick={() => openPhotoViewer(visible.map(toGalleryImage), visible.indexOf(img))}
                  style={{ cursor: 'pointer' }}
                />
              )}
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                {variant.showBadge && (
                  <span className="inline-flex w-fit rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Reference
                  </span>
                )}
                <span className="break-words text-xs">{img.display_name || img.name}</span>
              </div>
              {editable && img.disposition !== 'exclude' && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={isOn}
                  aria-label={`Include ${img.name ?? 'reference image'} on this test`}
                  disabled={disabled}
                  onClick={() => toggle(img)}
                  className="text-xs underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isOn ? 'Remove from Report' : 'Add to Report'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default TestItemReferenceImages;
